import React, { useState, useEffect } from 'react';
import FormRenderer from './FormRenderer';
import axios from 'axios';

const SchoolLunchApplicationForm = ({ studentIntakeId, onComplete, onCancel }) => {
  // For standalone PDF form, studentIntakeId is optional
  const effectiveStudentIntakeId = studentIntakeId;
  console.log('🎯 SchoolLunchApplicationForm: Component loaded');
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formJson, setFormJson] = useState(null);

  // Load the school lunch application form JSON
  useEffect(() => {
    const loadForm = async () => {
      try {
        const response = await axios.get('/api/forms/school-lunch-application');
        setFormJson(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading school lunch form:', error);
        setError('Failed to load form');
        setLoading(false);
      }
    };

    loadForm();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Generate PDF directly with form data
      await generatePDF(formValues);
      onComplete && onComplete({ success: true });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(error.response?.data?.error || 'Failed to generate PDF');
    } finally {
      setSaving(false);
    }
  };

  // Generate PDF from the submitted data
  const generatePDF = async (data) => {
    try {
      const response = await axios.post('/api/forms/school-lunch-application/generate-pdf', {
        formData: data
      }, {
        responseType: 'blob'
      });

      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `school_lunch_application_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error; // Re-throw so the caller can handle it
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading School Lunch Application...</p>
      </div>
    );
  }

  if (error && !formJson) {
    return (
      <div className="error-container">
        <h3>Error Loading Form</h3>
        <p>{error}</p>
        <button onClick={onCancel}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="school-lunch-application-form">
      <div className="form-header">
        <h2>School Lunch Application</h2>
        <p>Application for Free and Reduced Price School Meals</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <FormRenderer
          formData={formJson}
          formValues={formValues}
          onChange={setFormValues}
          errors={{}}
          readOnly={saving}
          userRole="parent"
          hideFormYear
        />
        <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || !formJson}
          >
            {saving ? 'Submitting...' : 'Submit & Generate PDF'}
          </button>
        </div>
      </form>

      <div className="form-footer">
        <p className="disclaimer">
          This form will generate a PDF that matches the official Missouri School Lunch Application format.
          The PDF will be automatically downloaded after submission.
        </p>
      </div>
    </div>
  );
};

export default SchoolLunchApplicationForm;
